import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
 MessageSquare,
 CheckCircle,
 XCircle,
 Clock,
 Calendar,
 Eye,
 Trash2,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminMessages } from '../../hooks/useAdminMessages';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminMessages = () => {
 const navigate = useNavigate();
 const {
 messages,
 isLoading,
 error,
 filters,
 readStatusOptions,
 messageTypeOptions,
 handleFilterChange,
 handleSearch,
 statistics,
 deleteMessage
 } = useAdminMessages();

 const columns = [
 {
 key: 'id',
 label: 'Message ID',
 sortable: true,
 width: 'w-32',
 render: (message) => (
 <span className="font-mono text-sm font-medium text-ink">
 #{message.id.substring(0, 8)}
 </span>
 )
 },
 {
 key: 'sender',
 label: 'From',
 sortable: true,
 width: 'w-48',
 render: (message) => (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {message.sender?.name || 'System'}
 </p>
 <p className="text-xs text-ink-tertiary truncate">
 {message.sender?.email || ''}
 </p>
 </div>
 )
 },
 {
 key: 'receiver',
 label: 'To',
 sortable: true,
 width: 'w-48',
 render: (message) => (
 <div className="min-w-0">
 <p className="text-sm font-medium text-ink truncate">
 {message.receiver?.name || 'N/A'}
 </p>
 <p className="text-xs text-ink-tertiary truncate">
 {message.receiver?.email || ''}
 </p>
 </div>
 )
 },
 {
 key: 'message',
 label: 'Message',
 sortable: false,
 width: 'w-64',
 render: (message) => (
 <p className="text-sm text-ink truncate">
 {message.message || 'N/A'}
 </p>
 )
 },
 {
 key: 'booking',
 label: 'Booking',
 sortable: true,
 width: 'w-32',
 render: (message) => (
 message.booking ? (
 <button
 onClick={() => navigate(`/admin/bookings/${message.booking_id}`)}
 className="font-mono text-sm font-medium text-ink hover:underline"
 >
 #{message.booking.booking_reference}
 </button>
 ) : (
 <span className="text-sm text-gray-400">N/A</span>
 )
 )
 },
 {
 key: 'created_at',
 label: 'Date',
 sortable: true,
 width: 'w-32',
 render: (message) => (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(message.created_at, 'medium')}
 </div>
 <div className="text-xs text-ink-tertiary">
 {formatTime(message.created_at)}
 </div>
 </div>
 </div>
 )
 },
 {
 key: 'is_read',
 label: 'Status',
 sortable: true,
 width: 'w-24',
 render: (message) => (
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ message.is_read ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800' }`}>
 {message.is_read ? 'Read' : 'Unread'}
 </span>
 )
 }
 ];

 const actions = [
 {
 label: 'View',
 icon: Eye,
 variant: 'default',
 onClick: (message) => {
 console.log('View message:', message);
 }
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (message) => {
 if (window.confirm('Are you sure you want to delete this message?')) {
 deleteMessage(message.id);
 }
 }
 }
 ];

 const { totalMessages, unreadMessages, readMessages } = statistics;

 if (error) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Messages</h3>
 <p className="text-red-600 text-sm">
 {error?.response?.data?.message || 'Failed to load messages. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Messages"
 subtitle="Manage and monitor all platform messages."
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.messages.title}
 steps={ADMIN_PAGE_GUIDES.messages.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Messages</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{totalMessages}</p>
 </div>
 <MessageSquare className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Unread</p>
 <p className="text-2xl font-bold text-amber-600 mt-1 tabular-nums">{unreadMessages}</p>
 </div>
 <Clock className="w-8 h-8 text-amber-500" />
 </div>
 </div>
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Read</p>
 <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">{readMessages}</p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 </div>

 <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Read Status"
 value={filters.is_read}
 onChange={(value) => handleFilterChange('is_read', value)}
 options={readStatusOptions}
 />
 <SearchSelect
 label="Message Type"
 value={filters.message_type}
 onChange={(value) => handleFilterChange('message_type', value)}
 options={messageTypeOptions}
 />
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 />
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading messages…</p>
 </div>
 ) : (
 <DataTable
 data={messages}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search messages by content, sender, receiver, or booking..."
 onSearch={handleSearch}
 emptyMessage="No messages found"
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>
 </div>
 );
};

export default AdminMessages;
